import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { loadDBFromSupabase } from '@/lib/supabase'

// Same localStorage key the CMS (src/cms/CMS.tsx) reads and writes to.
// This is what makes the storefront and the admin panel show the SAME data.
const DB_KEY = 'novacms_db'

// ─── Shapes coming out of the CMS DB blob ──────────────────────────────────
interface CmsProduct {
  id: string
  name: string
  price: number
  compare?: number
  catId: string
  stock: number
  sku?: string
  desc?: string
  status: string
  images: string[]
  tags?: string[]
  updatedAt?: number
}

interface CmsCategory {
  id: string
  name: string
  desc?: string
}

interface CmsDB {
  products?: CmsProduct[]
  categories?: CmsCategory[]
}

// ─── Shapes the storefront pages actually use ──────────────────────────────
export interface StoreProduct {
  id: string
  name: string
  price: number
  oldPrice: number | null
  image: string
  images: string[]
  description: string
  catId: string
  category: string // slugified category, e.g. "house" from "cat-house"
  badge: string | null
  rating: number
  reviews: number
  stock: number
}

export interface StoreCategory {
  id: string
  slug: string
  name: string
  desc: string
  count: number
}

type CatalogContextValue = {
  products: StoreProduct[]
  categories: StoreCategory[]
  loading: boolean
  getProduct: (id: string) => StoreProduct | undefined
}

const CatalogContext = createContext<CatalogContextValue>({
  products: [],
  categories: [],
  loading: true,
  getProduct: () => undefined,
})

function categorySlug(catId: string): string {
  return catId.replace(/^cat-/, '')
}

function mapProducts(cmsProducts: CmsProduct[] | undefined, cmsCategories: CmsCategory[] | undefined): StoreProduct[] {
  if (!cmsProducts) return []
  return cmsProducts
    .filter((p) => p.status !== 'draft' && p.status !== 'archived')
    .map((p) => ({
      id: p.id,
      name: p.name,
      price: Number(p.price) || 0,
      oldPrice: p.compare && Number(p.compare) > Number(p.price) ? Number(p.compare) : null,
      image: p.images?.[0] || '',
      images: p.images?.length ? p.images : [''],
      description: p.desc || '',
      catId: p.catId,
      category: categorySlug(p.catId || ''),
      badge: p.tags?.[0] ? capitalize(p.tags[0]) : null,
      rating: 5,
      reviews: 0,
      stock: Number(p.stock) || 0,
    }))
}

function mapCategories(cmsCategories: CmsCategory[] | undefined, cmsProducts: CmsProduct[] | undefined): StoreCategory[] {
  if (!cmsCategories) return []
  return cmsCategories.map((c) => ({
    id: c.id,
    slug: categorySlug(c.id),
    name: c.name,
    desc: c.desc || '',
    count: (cmsProducts || []).filter((p) => p.catId === c.id && p.status !== 'draft' && p.status !== 'archived').length,
  }))
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function readLocal(): CmsDB | null {
  try {
    const s = localStorage.getItem(DB_KEY)
    if (!s) return null
    return JSON.parse(s) as CmsDB
  } catch {
    return null
  }
}

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<StoreProduct[]>(() => {
    const local = readLocal()
    return mapProducts(local?.products, local?.categories)
  })
  const [categories, setCategories] = useState<StoreCategory[]>(() => {
    const local = readLocal()
    return mapCategories(local?.categories, local?.products)
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    loadDBFromSupabase().then((remote) => {
      if (cancelled) return
      const db = remote as CmsDB | null
      // Smart merge: only trust remote products/categories if they're actually
      // populated. If Supabase returned an empty or stale row (e.g. a save
      // failed to sync, or the table was just created), keep whatever we
      // already loaded from localStorage instead of wiping the page blank.
      if (db?.products?.length) {
        setProducts(mapProducts(db.products, db.categories))
      }
      if (db?.categories?.length) {
        setCategories(mapCategories(db.categories, db.products))
      }
      setLoading(false)
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })

    // Pick up CMS edits saved from the admin panel in another tab (or the same
    // tab, since the CMS manually dispatches this event on every save).
    const onStorage = (e: StorageEvent) => {
      if (e.key === DB_KEY && e.newValue) {
        try {
          const db = JSON.parse(e.newValue) as CmsDB
          setProducts(mapProducts(db.products, db.categories))
          setCategories(mapCategories(db.categories, db.products))
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener('storage', onStorage)
    return () => {
      cancelled = true
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const getProduct = (id: string) => products.find((p) => p.id === id)

  return (
    <CatalogContext.Provider value={{ products, categories, loading, getProduct }}>
      {children}
    </CatalogContext.Provider>
  )
}

export const useCatalog = () => useContext(CatalogContext)
